import { useState, useEffect } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { tierChangeApi, type TierChangePreview } from '../../api/tierChangeApi';
import { useCurrentOrganization } from '../../hooks/useCurrentOrganization';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { getTierById, formatPrice } from '../../constants/tiers';
import { useQueryClient } from '@tanstack/react-query';

interface TierChangeConfirmScreenProps {
  newTierId: string; // Tier ID to change to
  autoRenewal?: boolean; // Auto-renewal flag (default: false)
}

export function TierChangeConfirmScreen({ newTierId, autoRenewal = false }: TierChangeConfirmScreenProps) {
  const { popScreen } = useTabNavigationStore();
  const { data: organization } = useCurrentOrganization();
  const queryClient = useQueryClient();

  const [preview, setPreview] = useState<TierChangePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newTier = getTierById(newTierId);

  useEffect(() => {
    if (!organization?.id) return;

    const fetchPreview = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await tierChangeApi.preview(organization.id, newTierId);
        setPreview(data);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Не удалось рассчитать preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [organization?.id, newTierId]);

  const handleConfirm = async () => {
    if (!organization?.id || !preview) return;

    try {
      setConfirming(true);
      setError(null);

      // Confirm tier change (stub: treated as if payment succeeded)
      await tierChangeApi.confirm(organization.id, preview);

      // Invalidate organization query to refetch updated data
      await queryClient.invalidateQueries({ queryKey: ['organization', organization.id] });
      await queryClient.invalidateQueries({ queryKey: ['currentOrganization'] });

      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } catch {}

      alert(`Тариф успешно изменён на ${newTier?.name}!`);
      popScreen(); // Go back to plans screen
      popScreen(); // Go back to subscription manage screen
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Не удалось подтвердить смену тарифа');
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      } catch {}
    } finally {
      setConfirming(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Расчёт стоимости...</p>
      </div>
    );
  }

  if (error && !preview) {
    return (
      <div className="p-4 space-y-4">
        <PageHeader title="Ошибка" />
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
        <Button onClick={() => popScreen()} variant="secondary" fullWidth>
          Назад
        </Button>
      </div>
    );
  }

  if (!preview || !newTier) {
    return null;
  }

  const isFreeTrial = preview.trial_days_granted > 0;
  const isDowngrade = !preview.is_upgrade && preview.current_tier !== 'free';

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Подтверждение смены тарифа"
        subtitle="Проверьте детали перед подтверждением"
      />

      {error && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
      )}

      {/* New Tier Info */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          Новый тариф
        </h3>
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
            {newTier.name}
          </span>
          <span className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            {formatPrice(preview.new_price)}
          </span>
        </div>
      </Card>

      {/* Trial Notice */}
      {isFreeTrial && (
        <Card>
          <div className="text-center py-2">
            <p className="text-sm font-semibold" style={{ color: 'var(--accent)' }}>
              🎉 Вы получаете {preview.trial_days_granted} дней бесплатного триала!
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>
              Полный доступ ко всем функциям тарифа {newTier.name}
            </p>
          </div>
        </Card>
      )}

      {/* Calculation Details (for paid tier changes) */}
      {!isFreeTrial && preview.current_tier !== 'free' && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Детали расчёта
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Текущий тариф:</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-medium">
                {preview.current_tier.toUpperCase()} ({formatPrice(preview.current_price)})
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Осталось дней:</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-medium">
                {preview.days_remaining} дней
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Остаток средств:</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-medium">
                {preview.remaining_value.toFixed(2)} ₽
              </span>
            </div>
            <div className="h-px bg-[var(--separator)] my-2" />
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Новая длительность:</span>
              <span style={{ color: 'var(--accent)' }} className="font-semibold">
                {preview.new_duration} дней
              </span>
            </div>
            {isDowngrade && (
              <p className="text-xs mt-2" style={{ color: 'var(--text-hint)' }}>
                При переходе на более дешёвый тариф остаток конвертируется в дополнительные дни
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Summary */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          Итого
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span style={{ color: 'var(--text-hint)' }}>К оплате:</span>
            <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
              {preview.charge_amount === 0 ? 'Бесплатно' : `${preview.charge_amount} ₽`}
            </span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'var(--text-hint)' }}>Активен до:</span>
            <span style={{ color: 'var(--text-primary)' }} className="font-medium">
              {formatDate(preview.new_expires_at)}
            </span>
          </div>
          {autoRenewal && (
            <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--separator)' }}>
              <span style={{ color: '#22c55e' }}>✓</span>
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Автопродление включено
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Stub Notice */}
      {preview.charge_amount > 0 && (
        <Card>
          <p className="text-xs text-center" style={{ color: 'var(--text-hint)' }}>
            ⚠️ Временная заглушка: нажатие "Подтвердить" сразу активирует тариф без реальной оплаты.
            Интеграция с ЮKassa будет добавлена позже.
          </p>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={() => popScreen()} variant="secondary" fullWidth disabled={confirming}>
          Отмена
        </Button>
        <Button onClick={handleConfirm} disabled={confirming} fullWidth>
          {confirming ? 'Подтверждение...' : 'Подтвердить'}
        </Button>
      </div>
    </div>
  );
}
